import { gsap } from "gsap";
import { focusHelper, removeFocusHelper } from "../helpers/focus-helper.js";

class MainHeader extends HTMLElement {
    constructor() {
        super();
    }

    static get observedAttributes() {
        return ["data-drawer-open"];
    }

    connectedCallback() {
        this.buttonOpen = this.querySelector("#main-navigation-button");
        this.buttonClose = this.querySelector("#close-navigation-button");
        this.toggleHandle = this.toggle.bind(this);
        this.closeHandle = this.close.bind(this);
        this.drawer = this.querySelector("#drawer");
        this.dropdownContainers = Array.from(this.querySelectorAll(".drawer-navigation .dropdown-container"));
        this.dropdownButtons = Array.from(this.querySelectorAll(".drawer-navigation .has-dropdown  button"));
        this.desktopDropdownContainers = Array.from(
            this.querySelectorAll(".desktop-navigation-container .has-dropdown")
        );
        this.header = document.getElementById("main-header");
        this.sticky = document.getElementById("sticky-header");
        this.mobileLines = this.querySelectorAll(".anim-line");
        this.headerBounds = {};
        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.addEventListener("scroll", this.onScrollHandler, false);
        this.createObserver();

        this.buttonOpen.addEventListener("click", this.toggleHandle);

        if (this.isClosed) {
            this.buttonClose.addEventListener("click", this.closeHandle);
        }

        this.dropdownSetup();
        this.dropdowns();
        this.mediaQuery();
        this.desktopDropdownsEvents();

        this.linesTl = gsap.timeline({ paused: true });
        this.linesTl.from(this.mobileLines, { scaleX: 0, stagger: 0.03, ease: "power2.out" });
    }

    get currentState() {
        return this.getAttribute("data-drawer-open");
    }

    get isClosed() {
        return this.getAttribute("data-drawer-open") === "false";
    }

    toggle() {
        if (this.isClosed) {
            this.open();
        } else {
            this.close();
        }
    }

    open() {
        console.log("open");
        this.linesTl.play();
        this.setAttribute("data-drawer-open", "true");
        const drawer = this.drawer;
        setTimeout(() => {
            focusHelper(drawer);
        }, 1000);
    }

    close() {
        this.linesTl.reverse();
        this.setAttribute("data-drawer-open", "false");
        //const drawer = this.drawer;
        setTimeout(() => {
            removeFocusHelper();
        }, 1000);
    }

    openDrawer() {
        this.buttonOpen.setAttribute("aria-expanded", "true");
        document.documentElement.style.overflow = "hidden";
    }

    closeDrawer() {
        this.buttonOpen.setAttribute("aria-expanded", "false");
        document.documentElement.style.overflow = "auto";
        this.dropdownButtons.forEach((button) => {
            button.setAttribute("aria-expanded", "false");
            button.nextElementSibling.style.height = 0;
        });
        //emoveFocusHelper(this.buttonOpen);
    }

    attributeChangedCallback(name, oldState, newState) {
        if (newState === "true") this.openDrawer();
        if (oldState === "true" && newState === "false") this.closeDrawer();
    }

    desktopDropdownsEvents() {
        this.desktopDropdownContainers.forEach((dropdownContainer) => {
            dropdownContainer.addEventListener("mouseenter", (event) => {
                event.stopPropagation();
                const dropdownButton = dropdownContainer.firstElementChild;
                const dropdownList = document.getElementById(dropdownButton.getAttribute("aria-controls"));

                const schemaContainer = document.querySelector("schema-container[open=true]")
                    ? document.querySelector("schema-container")
                    : false;

                if (dropdownButton.getAttribute("aria-expanded") === "false") {
                    if (schemaContainer) {
                        schemaContainer.close();
                    }
                    dropdownButton.setAttribute("aria-expanded", "true");
                    this.header.classList.add("dropdown-expanded");
                }
            });
            dropdownContainer.addEventListener("mouseleave", (event) => {
                const dropdownButton = dropdownContainer.firstElementChild;
                event.stopPropagation();
                if (dropdownButton.getAttribute("aria-expanded") === "true") {
                    dropdownButton.setAttribute("aria-expanded", "false");
                    this.header.classList.remove("dropdown-expanded");
                }
            });
            const dropdownButton = dropdownContainer.firstElementChild;

            dropdownButton.addEventListener("keydown", (event) => {
                const key = event.code.toUpperCase();
                const dropdownList = document.getElementById(dropdownButton.getAttribute("aria-controls"));
                const isClosed = dropdownButton.getAttribute("aria-expanded") === "false";
                const openers = ["SPACE", "ENTER"];

                if (key === "ESCAPE" && !isClosed) {
                    dropdownButton.setAttribute("aria-expanded", "false");
                    this.header.classList.remove("dropdown-expanded");
                    removeFocusHelper(dropdownButton);
                }

                if (openers.includes(key) && isClosed) {
                    dropdownButton.setAttribute("aria-expanded", "true");
                    this.header.classList.add("dropdown-expanded");
                    focusHelper(dropdownList);
                    this.dropdownOpenedByKeyboard(dropdownList, dropdownButton);
                }
            });
        });
    }

    dropdownOpenedByKeyboard(dropdown, dropdownButton) {
        dropdown.addEventListener("keyup", (event) => {
            const key = event.code.toUpperCase();
            const isOpened = dropdownButton.getAttribute("aria-expanded") === "true";

            if (key === "ESCAPE" && isOpened) {
                dropdownButton.setAttribute("aria-expanded", "false");
                this.header.classList.remove("dropdown-expanded");
                removeFocusHelper(dropdownButton);
            }
        });
    }

    dropdownSetup() {
        this.dropdownContainers.forEach((dropdown) => {
            const subMenuHeight = dropdown.firstElementChild.offsetHeight;
            dropdown.style.setProperty("--dropdown-content-height", `${subMenuHeight}px`);
            dropdown.setAttribute("data-dropdown", `${subMenuHeight}px`);
            dropdown.style.height = 0;
        });
    }

    dropdowns() {
        this.dropdownButtons.forEach((button) => {
            button.addEventListener("click", () => {
                const siblings = Array.from(this.querySelectorAll(".navigation-list [aria-controls]"));
                const siblingsOpened = siblings.filter((sibling) => sibling.getAttribute("aria-expanded") === "true");
                const wasOpened = button.getAttribute("aria-expanded") === "true";
                const dropdownHeight = button.nextElementSibling.getAttribute("data-dropdown");
                const dropdown = button.nextElementSibling;

                if (siblingsOpened.length === 1 && siblingsOpened[0] === button) {
                    button.setAttribute("aria-expanded", "true");
                    dropdown.style.height = dropdownHeight;
                } else {
                    if (siblingsOpened.length > 0) {
                        siblingsOpened[0].setAttribute("aria-expanded", "false");
                        siblingsOpened[0].nextElementSibling.style.height = 0;
                    }
                }

                if (wasOpened) {
                    button.setAttribute("aria-expanded", "false");
                    dropdown.style.height = 0;
                } else {
                    button.setAttribute("aria-expanded", "true");
                    dropdown.style.height = dropdownHeight;
                }
            });
        });
    }

    mediaQuery() {
        const query = window.matchMedia("(min-width: 1024px)");
        query.addEventListener("change", (event) => {
            if (this.isClosed) return;
            if (event.matches && this.isClosed === false) {
                this.close();
            }
        });
    }

    createObserver() {
        let observer = new IntersectionObserver((entries, observer) => {
            this.headerBounds = entries[0].intersectionRect;
            observer.disconnect();
        });

        observer.observe(this.header);
    }

    onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
            this.sticky.classList.add("scrolled-past-header");
            if (this.preventHide) return;
            requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
            this.sticky.classList.add("scrolled-past-header");
            if (!this.preventReveal) {
                requestAnimationFrame(this.reveal.bind(this));
            } else {
                window.clearTimeout(this.isScrolling);

                this.isScrolling = setTimeout(() => {
                    this.preventReveal = false;
                }, 66);

                requestAnimationFrame(this.hide.bind(this));
            }
        } else if (scrollTop <= this.headerBounds.top) {
            this.sticky.classList.remove("scrolled-past-header");
            requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
    }

    hide() {
        this.sticky.classList.add("header-hidden");
    }

    reveal() {
        this.sticky.classList.add("header-sticky", "header-animate");
        this.sticky.classList.remove("header-hidden");
    }

    reset() {
        this.sticky.classList.remove("header-sticky", "header-hidden", "header-animate");
    }
}

customElements.define("main-header", MainHeader);
